// react
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// components
import { Button, Icon, Input } from '@/components/atoms';
import { Message, TypingDots } from '@/components/organisms';

// models
import { Account, ChatMessage } from '@karehero/models';

interface ChatProps {
  className?: string;
  messages?: ChatMessage[];
  onSend?: (message: string) => Promise<void>;
  isTyping?: boolean;
  account?: Account;
}

export const Chat = ({
  className,
  messages,
  onSend,
  isTyping,
  account,
}: ChatProps) => {
  // state
  const [message, setMessage] = useState('');

  // refs
  const ref = useRef<HTMLDivElement>(null);

  // hooks
  const navigate = useNavigate();

  // methods
  const handleSendMessage = useCallback(async () => {
    if (!message || isTyping) return;
    setMessage('');

    try {
      await onSend?.(message);
    } catch (error) {
      console.error(error);
    }
  }, [message, onSend, isTyping]);

  // effects
  useEffect(() => {
    ref.current?.scrollIntoView({});
  }, [messages, isTyping]);

  return (
    <Wrapper className={className}>
      <BackButtonContainer>
        <Button
          ariaLabel='Back'
          size='sm'
          variant='secondary'
          iconLeft={'chevron-left'}
          onPress={() => navigate('/messenger')}
          isInline
        >
          Back
        </Button>
      </BackButtonContainer>
      <Body>
        {/* <Card color='red' isPaddingSm>
            <Disclaimer>
              KareHero AI can make mistakes. Check important info.
            </Disclaimer>
          </Card> */}
        {/* <ChatStartHelper>
            This is the beginning of your conversation with KareHero AI.
          </ChatStartHelper> */}
        {messages?.map((message) => (
          <Message
            key={message.id}
            isSender={message.role === 'user'}
            value={message.content}
            account={account}
            links={message.links}
          />
        ))}
        {isTyping && (
          <Message>
            <TypingDots />
          </Message>
        )}
        <AlwaysScrollToBottom ref={ref} />
      </Body>
      <Footer>
        <MessageBox
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <StyledInput
            placeholder='Help me with...'
            value={message}
            onChange={(val) => setMessage(val)}
            autoFocus
          />
          <SendButton type='submit' disabled={!message || isTyping}>
            <Icon icon='paper-plane-top' fontSize={24} type='fas' />
          </SendButton>
        </MessageBox>
      </Footer>
    </Wrapper>
  );
};

const StyledInput = styled(Input)`
  border: none !important;
  outline: none !important;
  & input {
    border: none !important;
    outline: none !important;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
`;

const Body = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[16]};
  background: ${(props) => props.theme.color.neutral[10]};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[8]}
    ${(props) => props.theme.spacing[16]};
  padding-bottom: ${(props) => props.theme.spacing[12]};
  border-top: 1px solid ${(props) => props.theme.color.neutral[40]};
`;

const MessageBox = styled.form`
  display: flex;
  gap: ${(props) => props.theme.spacing[16]};
  width: 100%;
`;

const SendButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => props.theme.color.primary[30]};

  transition-duration: 200ms;
  &:hover {
    color: ${(props) => props.theme.color.primary[80]};
  }

  &:disabled {
    cursor: default;
    color: ${(props) => props.theme.color.neutral[50]};
  }
`;

const Disclaimer = styled.div`
  font: ${(props) => props.theme.typography.body.xxs.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  padding: 0 ${(props) => props.theme.spacing[16]};
  text-align: center;
`;

const AlwaysScrollToBottom = styled.div`
  height: 0;
  max-height: 0;
`;

const ChatStart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const ChatStartHelper = styled.div`
  text-align: center;
  font: ${(props) => props.theme.typography.body.xxs.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

const BackButtonContainer = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  padding: ${(props) => props.theme.spacing[12]};
  z-index: 10;
  background: ${(props) => props.theme.color.neutral[10]};
  width: fit-content;
`;

Chat.displayName = 'Chat';

export default Chat;
